import {
  ApplicationLicense,
  Machine,
  ScratchpadBucket,
} from '../../../../../api/graphql/generated';
import { logger } from '../../../../../util/logger';
import {
  ApplicationDefaultParameterSetData,
  ApplicationDefaultParameterSetTagToEquipmentMapping,
  Flow,
  FlowItem,
  FlowItemParameterRef,
  Tool,
} from '../../../shared/model/flow.types';
import { AbstractFlowService } from '../../../shared/service/abstract-flow.service';
import {
  FlowItemService,
  FlowRuleService,
} from '../../../shared/service/service.types';
import { BeaconPaintingData } from '../model/beacon-painting-data';

class PaintingFlowServiceImpl extends AbstractFlowService<
  BeaconPaintingData.FlowConfig,
  typeof BeaconPaintingData.FlowSectionType
> {
  constructor(
    private license: ApplicationLicense,
    flowItemService: FlowItemService<
      BeaconPaintingData.FlowConfig,
      typeof BeaconPaintingData.FlowSectionType
    >,
    flowRuleService: FlowRuleService,
    tools: Tool[],
    protected machine: Machine
  ) {
    super(
      tools,
      flowItemService,
      flowRuleService,
      BeaconPaintingData.FlowValidationRules,
      machine
    );
  }

  public getDefaultFlowConfig(): BeaconPaintingData.FlowConfig {
    const license = this.getLicense();

    const tools = this.getToolsForAssignedEquipment();
    if (tools.length === 0) {
      logger.error('no tools found', { license });
    }

    const defaultTool = tools[0] ?? {};

    const equipmentConfig = this.getFlowConfigForEquipment();

    return {
      home: {
        // TODO put proper home position
        waypoint: {
          x: 0.608,
          y: -0.532,
          z: 0.799,
          rx: 0.07,
          ry: 1.5,
          rz: -0.226,
        },
        qnear: [-0.519, 0.342, 0.408, 0.065, -1.537, 0.328],
      },
      ioMapping: this.getDefaultIOMapping(),
      applicatorTool: {
        ...defaultTool.config,
      },
      betweenSpeed: 0.25,
      applicator: {
        model: license.painter,
        ioMap: {
          mode: 'DO5',
          trigger: 'DO4',
        },
      },
      ...equipmentConfig,
    };
  }

  public async isFlowValidForTeaching(flow: Flow): Promise<boolean> {
    const configValid = flow.config !== undefined;
    const applicationDefaultParameterSet =
      flow.defaultParameterSet !== undefined;

    return configValid && applicationDefaultParameterSet;
  }

  public getAvailableFlowItemParameterRefs(): FlowItemParameterRef[] {
    return Object.values(BeaconPaintingData.FlowItemParameterRef);
  }

  public getApplicationDefaultParameterSetTagToConfig(): Record<
    string,
    string
  > {
    // TODO: Implement when we have parameter set tags
    return {};
  }

  public getApplicationDefaultParameterSetTagToEquipmentMapping(): ApplicationDefaultParameterSetTagToEquipmentMapping {
    // TODO: Implement when we have equipment-specific parameter set tags
    return {};
  }

  public getApplicationDefaultParameterSetImplicitTags(
    _item: FlowItem<string>
  ): Record<string, unknown> {
    return {
      painter: this.getEquipmentName(),
    };
  }

  public getApplicationDefaultParameterSetSubheadingFromBucket(
    bucket: ScratchpadBucket
  ): string {
    const subheadingParts: string[] = [];

    const parameterSetData = bucket.data as ApplicationDefaultParameterSetData;

    const item: FlowItem = {
      ref: BeaconPaintingData.FlowItemRef.PAINT,
      parameters: parameterSetData.parameters,
    };

    // TODO: Add paint-specific subheading details when we have more parameters

    return subheadingParts.join(', ');
  }

  public getEquipmentName(): string {
    return this.getLicense().painter;
  }

  protected getLicense(): BeaconPaintingData.License {
    return this.license.licenseFormValues as BeaconPaintingData.License;
  }

  protected getFlowConfigForEquipment(): Partial<BeaconPaintingData.FlowConfig> {
    const equipment = this.getEquipmentName();

    if (equipment === BeaconPaintingData.Painter.NORDSON_ENCORE_GEN3) {
      return {
        // TODO: Add equipment-specific config when we have paint parameters
      };
    }

    return {};
  }
}

export { PaintingFlowServiceImpl };
